import { useEffect, useState, type MouseEvent } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { ModuleLogoutButton } from "@itaas/core/auth/ModuleLogoutButton";
import { Breadcrumb } from "@/components/Breadcrumb";
import { PageMetaProvider, usePageMetaContext } from "@/context/PageMetaContext";
import { bindDemoPrescriptionToIscProducts } from "@/core/clinical/demo-clinical-bootstrap";
import { seedPharmacyFulfillmentDemo } from "@/core/clinical/demo-pharmacy-fulfillment";
import { seedDemoHospitolCommercial } from "@/core/demo/demo-hospitol-commercial";
import { seedDemoHospitolRich } from "@/core/demo/demo-hospitol-rich";
import { isDemoModeEnabled } from "@/core/demo/is-demo-mode-enabled";
import {
  buildBreadcrumbTrail,
  resolvePageTitle,
} from "@/core/navigation/breadcrumb-trail";
import { isNavActive, useHospitolSidebarItems } from "@/core/navigation/useSidebarItems";
import { HospitolNotificationBell } from "@/components/HospitolNotificationBell";
import { IscOfflineBanner } from "@/components/IscOfflineBanner";
import { getSuiteModulesUrl } from "@/core/config";
import { getTenantDisplayName } from "@/core/tenant-display";
import { useIscOffline } from "@/hooks/useIscOffline";
import {
  recordAuthAuditEvent,
  syncTenantEditionProfile,
} from "@/core/security/sync-tenant-edition";

function TenantLayoutShell() {
  const { user } = useAuth();
  const location = useLocation();
  const { meta } = usePageMetaContext();
  const sections = useHospitolSidebarItems();
  const breadcrumb = buildBreadcrumbTrail(location.pathname, meta);
  const title = resolvePageTitle(location.pathname, meta);
  const establishmentName = getTenantDisplayName();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const iscOffline = useIscOffline(Boolean(user));

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    void syncTenantEditionProfile();
    void recordAuthAuditEvent("session_resume");
  }, [user]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSidebarOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [sidebarOpen]);

  const closeSidebarOnMobileLink = (event: MouseEvent<HTMLElement>) => {
    if (!window.matchMedia("(max-width: 991.98px)").matches) return;
    if ((event.target as HTMLElement).closest("a")) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (!user || !isDemoModeEnabled()) return;
    void (async () => {
      seedDemoHospitolRich();
      await bindDemoPrescriptionToIscProducts();
      await seedPharmacyFulfillmentDemo();
      await seedDemoHospitolCommercial();
    })();
  }, [user?.id]);

  return (
    <div className={`ph-layout${sidebarOpen ? " ph-layout--sidebar-open" : ""}`}>
      <button
        type="button"
        className="ph-sidebar-backdrop"
        aria-label="Fermer le menu de navigation"
        tabIndex={sidebarOpen ? 0 : -1}
        onClick={() => setSidebarOpen(false)}
      />
      <aside
        id="ph-app-sidebar"
        className="ph-sidebar"
        aria-label="Navigation principale"
        onClick={closeSidebarOnMobileLink}
      >
        <div className="ph-sidebar-brand">
          <span className="ph-brand-icon" aria-hidden>
            H
          </span>
          <div>
            <div className="ph-brand-title">ITaaS Hospitol</div>
            <div className="ph-brand-tagline">Plateforme hospitalière</div>
          </div>
        </div>
        {sections.map((section) => (
          <div key={section.title}>
            <div className="ph-sidebar-section">{section.title}</div>
            {section.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`ph-sidebar-link ${
                  isNavActive(item.path, location.pathname) ? "active" : ""
                }`}
              >
                <span>{item.label}</span>
                {item.badge === "soon" && (
                  <span className="ph-sidebar-badge">Bientôt</span>
                )}
              </Link>
            ))}
          </div>
        ))}
      </aside>

      <div className="ph-main">
        <header className="ph-topbar">
          <div className="ph-topbar-left">
            <button
              type="button"
              className="ph-sidebar-toggle"
              aria-expanded={sidebarOpen}
              aria-controls="ph-app-sidebar"
              aria-label={
                sidebarOpen ? "Fermer le menu de navigation" : "Ouvrir le menu de navigation"
              }
              onClick={() => setSidebarOpen((open) => !open)}
            >
              <span className="ph-sidebar-toggle__icon" aria-hidden />
              <span className="ph-sidebar-toggle__label">Menu</span>
            </button>
            <div className="ph-topbar-headings">
              <h1 className="ph-topbar-title">{title}</h1>
              <span className="ph-topbar-sub">{establishmentName}</span>
            </div>
          </div>
          <div className="ph-topbar-actions d-flex align-items-center gap-2 gap-md-3">
            <HospitolNotificationBell />
            <div className="ph-user-chip">
              <span className="ph-user-avatar" aria-hidden>
                {(user?.firstName?.[0] ?? user?.email?.[0] ?? "U").toUpperCase()}
              </span>
              <span className="ph-user-email">{user?.email}</span>
            </div>
            <a href={getSuiteModulesUrl()} className="btn btn-sm ph-btn-outline ph-topbar-link">
              <span className="ph-topbar-link__full">Autres modules</span>
              <span className="ph-topbar-link__short">Modules</span>
            </a>
            <ModuleLogoutButton className="btn btn-sm btn-outline-secondary" />
          </div>
        </header>
        <IscOfflineBanner
          offline={iscOffline.offline}
          checking={iscOffline.checking}
          onRecheck={() => void iscOffline.recheck()}
        />
        <main className="ph-content">
          <Breadcrumb items={breadcrumb} />
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function TenantLayout() {
  return (
    <PageMetaProvider>
      <TenantLayoutShell />
    </PageMetaProvider>
  );
}
